import type { SkidListItem } from '@/api/skids/skids-types'

import { formatPackagingSkidBadgeLabel, formatShippingUnitName, toBackendUnitName } from './order-skids'

export type ResolveSkidInputError =
  | 'invalid'
  | 'not-found'
  | 'closed'
  | 'wrong-order'
  | 'wrong-type'
  | 'multiple-match'

export type ResolveSkidInputResult =
  | { ok: true; skid_id: null }
  | { ok: true; skid_id: number; skid?: SkidListItem }
  | { ok: false; code: ResolveSkidInputError; message: string }

/**
 * `bundle-target` — input resolves to the skid a bundle should be attached to.
 * Only single-order skids (LHB/SKI) are valid. Scanning a MOS/OUT in this mode
 * is a user mistake (bundles must go onto an LHB, and the LHB goes into a MOS).
 *
 * `lhb-container` — input resolves to an LHB that should be nested under a MOS/OUT.
 * Only LHB is valid; SKI, MOS, OUT all rejected.
 */
export type ResolveSkidExpect = 'bundle-target' | 'lhb-container'

export interface ResolveSkidContext {
  /**
   * Current order autoid — single-order SKI/LHB skids must match it. The
   * backend populates `skid.orders[]` with autoids (e.g. `912M54640Q18EN40`),
   * not invoice numbers, so this is the authoritative ownership key.
   */
  orderAutoid?: string
  /** Which kind of skid the caller is expecting. Defaults to `bundle-target`. */
  expect?: ResolveSkidExpect
}

function isMultiOrderType(skid: SkidListItem): boolean {
  return skid.skid_type === 'MOS' || skid.skid_type === 'OUT'
}

function skidBelongsToOrder(skid: SkidListItem, autoid: string): boolean {
  return skid.orders?.some((o) => o === autoid) ?? false
}

function validateSkid(
  skid: SkidListItem,
  ctx: ResolveSkidContext
): ResolveSkidResultInternal {
  const label = formatShippingUnitName(skid)
  const expect = ctx.expect ?? 'bundle-target'

  if (expect === 'bundle-target' && isMultiOrderType(skid)) {
    return {
      ok: false,
      code: 'wrong-type',
      message: `${label} is a ${skid.skid_type === 'MOS' ? 'MOS' : 'Outside Rack'} — attach the bundle to a package first, then place the package into ${label}.`
    }
  }
  if (expect === 'lhb-container' && skid.skid_type !== 'LHB') {
    return {
      ok: false,
      code: 'wrong-type',
      message: `${label} is not a package. Only packages (PKG) can be placed on a MOS / Outside Rack.`
    }
  }

  if (skid.is_complete === true) {
    return {
      ok: false,
      code: 'closed',
      message: `Shipping unit ${label} is already closed. Create a new one or reopen.`
    }
  }
  if (
    !isMultiOrderType(skid) &&
    ctx.orderAutoid != null &&
    !skidBelongsToOrder(skid, ctx.orderAutoid)
  ) {
    const other = skid.orders?.[0]?.split('-')[0]?.trim()
    return {
      ok: false,
      code: 'wrong-order',
      message: other
        ? `Shipping unit ${label} belongs to Order ${other}, not this order.`
        : `Shipping unit ${label} belongs to another order.`
    }
  }
  return { ok: true, skid_id: skid.id, skid }
}

type ResolveSkidResultInternal = ResolveSkidResult

type ResolveSkidResult = ResolveSkidInputResult

/**
 * Maps packaging input to `skid_id` for bundle multi-update.
 * - Empty → unassign (`null`).
 * - All digits → numeric skid id (matched against list when possible to run validations).
 * - Otherwise → match `name` (case-insensitive), then computed badge label (e.g. SKD-…, LHB-…).
 *
 * When `ctx.orderAutoid` is provided, single-order SKI/LHB skids must belong
 * to that order (matched against `skid.orders[]`, which the backend stores as
 * autoids). MOS/OUT accept bundles from any order. Closed skids are rejected.
 */
export function resolveSkidInputToId(
  raw: string,
  skids: SkidListItem[],
  ctx: ResolveSkidContext = {}
): ResolveSkidInputResult {
  const t = raw.trim()
  if (t === '') return { ok: true, skid_id: null }

  if (/^\d+$/.test(t)) {
    const n = Number.parseInt(t, 10)
    if (Number.isNaN(n) || n < 0) {
      return { ok: false, code: 'invalid', message: 'Skid id must be a non-negative whole number' }
    }
    const match = skids.find((s) => s.id === n)
    if (!match) {
      return {
        ok: false,
        code: 'not-found',
        message: 'Shipping unit not found in the current list. Check sticker and try again.'
      }
    }
    return validateSkid(match, ctx)
  }

  // A name read off the screen or a sticker (`BDL-…`) means the backend's `LHB-…`.
  const lower = toBackendUnitName(t).toLowerCase()

  const byName = skids.filter(
    (s) => s.name != null && s.name.trim().toLowerCase() === lower
  )
  if (byName.length === 1) return validateSkid(byName[0], ctx)
  if (byName.length > 1) {
    return {
      ok: false,
      code: 'multiple-match',
      message: 'Multiple skids share that name — enter the numeric skid id'
    }
  }

  const byLabel = skids.filter(
    (s) => formatPackagingSkidBadgeLabel(s).toLowerCase() === lower
  )
  if (byLabel.length === 1) return validateSkid(byLabel[0], ctx)
  if (byLabel.length > 1) {
    return {
      ok: false,
      code: 'multiple-match',
      message: 'Multiple skids match — enter the numeric skid id'
    }
  }

  return {
    ok: false,
    code: 'not-found',
    message: 'Shipping unit not found. Check sticker and try again.'
  }
}

/** Value to show in the input: label/name when we know the skid, else raw id until data loads. */
export function formatSkidInputDisplay(
  skid_id: number | null | undefined,
  skids: SkidListItem[]
): string {
  if (skid_id == null) return ''
  const s = skids.find((x) => x.id === skid_id)
  if (!s) return String(skid_id)
  const name = s.name?.trim()
  if (name) return name
  return formatPackagingSkidBadgeLabel(s)
}

/**
 * The unit name as people read it (`BDL-…` for a labelled LHB), for read-only cells.
 * Editable inputs keep `formatSkidInputDisplay` — what's typed there resolves by the real name.
 */
export function formatSkidDisplayName(skid_id: number | null | undefined, skids: SkidListItem[]): string {
  if (skid_id == null) return ''
  const s = skids.find((x) => x.id === skid_id)
  return s ? formatShippingUnitName(s) : String(skid_id)
}

/**
 * Strip control chars (NUL, CR, LF, Tab, etc.) and surrounding whitespace from
 * a scanner-typed string. HID-keyboard scanners frequently append CR/LF/Tab as
 * the "submit" suffix; on some Windows keyboard layouts a few of those land
 * inside the value rather than after it.
 */
function sanitizeScanInput(raw: string): string {
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[ -]+/g, '').trim()
}

/**
 * Decode a URL-encoded scan param without throwing on malformed sequences —
 * scanners on non-US keyboard layouts can produce partially-encoded strings.
 */
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Skid QR codes encode a packaging URL like `https://.../packaging?skid=<label>&...`.
 * When such a URL is typed into a scan input (scanner = HID keyboard), we want
 * the skid label, not the whole URL.
 *
 * Robustness goals (the scanner is unreliable):
 *  - Tolerate leading/trailing junk and stray control chars.
 *  - Tolerate `new URL` failing — fall back to a regex match for `skid=<label>`.
 *  - Tolerate missing scheme (`macoapp.ca/...`, `/packaging?skid=...`).
 *  - URL-decode the extracted label.
 *
 * Falls back to the raw (sanitized) value for legacy plain-label scans and
 * manual typing.
 */
export function extractSkidLabelFromInput(raw: string): string {
  const t = sanitizeScanInput(raw)
  if (!t) return ''

  // Fast path: a clean URL with a parseable `?skid=...` query.
  if (/^https?:\/\//i.test(t)) {
    try {
      const url = new URL(t)
      const skidParam = url.searchParams.get('skid')
      if (skidParam) return safeDecode(skidParam).trim()
    } catch {
      // Fall through to regex-based extraction below.
    }
  }

  // Regex fallback: works for malformed URLs, missing scheme, or junk-prefixed
  // strings (e.g. `xhttps://...`, `macoapp.ca/packaging?skid=LHB-12`,
  // `/packaging?skid=LHB-12`). Anchored on `skid=` so we don't accidentally
  // match other text.
  const m = t.match(/[?&\s/]skid=([^&\s#]+)/i) ?? t.match(/^skid=([^&\s#]+)/i)
  if (m && m[1]) {
    const label = safeDecode(m[1]).trim()
    if (label) return label
  }

  return t
}

/**
 * Bundle QR codes encode a packaging URL like `https://.../packaging/?bundle=<id>&...`.
 * Returns the numeric bundle id from such a URL, or from a raw numeric / `BUN-<id>`
 * token. Tolerates the same scanner quirks as `extractSkidLabelFromInput`.
 */
export function extractBundleIdFromInput(raw: string): number | null {
  const t = sanitizeScanInput(raw)
  if (!t) return null

  if (/^https?:\/\//i.test(t)) {
    try {
      const url = new URL(t)
      const bundleParam = url.searchParams.get('bundle')
      if (bundleParam) {
        const n = Number.parseInt(bundleParam, 10)
        if (Number.isFinite(n) && n > 0) return n
      }
    } catch {
      // Fall through to regex-based extraction below.
    }
  }

  // Regex fallback for malformed or scheme-less URLs.
  const fromQuery =
    t.match(/[?&\s/]bundle=(\d+)/i) ?? t.match(/^bundle=(\d+)/i)
  if (fromQuery && fromQuery[1]) {
    const n = Number.parseInt(fromQuery[1], 10)
    if (Number.isFinite(n) && n > 0) return n
  }

  const plain = t.match(/^(?:BUN[-_]?)?(\d+)$/i)
  if (plain) {
    const n = Number.parseInt(plain[1], 10)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}
